import React, { type ComponentType, type LazyExoticComponent } from 'react';

/**
 * A wrapper around React.lazy that handles ChunkLoadErrors gracefully.
 * If a chunk fails to load (likely due to a new deployment), it forces a page reload.
 * This ensures the user gets the new version without seeing an error screen.
 */
export const lazyImport = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> => {
    return React.lazy(async () => {
        try {
            return await factory();
        } catch (error: any) {
            const isChunkError =
                error?.name === 'ChunkLoadError' ||
                error?.message?.includes('Failed to fetch dynamically imported module') ||
                error?.message?.includes('Importing a module script failed');

            if (isChunkError) {
                console.log('Chunk missing. Reloading to fetch new version...');

                // Check against infinite loop (optional but recommended safety)
                const storageKey = `chunk_reload_${window.location.pathname}`;
                const lastReload = sessionStorage.getItem(storageKey);
                const now = Date.now();

                // Only reload if we haven't done so recently for this path (e.g., 5 seconds)
                if (!lastReload || now - parseInt(lastReload) > 5000) {
                    sessionStorage.setItem(storageKey, now.toString());
                    window.location.reload();

                    // Return a Promise that never resolves to keep the Suspense fallback visible
                    // while the page reloads. This prevents the ErrorBoundary from catching/showing the error.
                    return new Promise<{ default: T }>(() => { });
                }
            }

            // If it's not a chunk error or we're looping, throw it to be caught by ErrorBoundary
            throw error;
        }
    });
};
